import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import api from '../../../api/axiosInstance';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { toStartOfDayISO, toEndOfDayISO } from '../utils/dashboardDateUtils';

export type AveragePipelineTimeByStages = { stage: string; avg_duration_minutes: number };

type AverageTimeByStageApiItem = { stage: string; averageTimeInMinutes?: string | number };

function normalizeAverageTimeResponse(rows: AverageTimeByStageApiItem[]): AveragePipelineTimeByStages[] {
  return (rows ?? []).map((item) => ({
    stage: item.stage,
    avg_duration_minutes: Number(item.averageTimeInMinutes) || 0
  }));
}

export async function getAveragePipelineTimeByStage(startDate: string, endDate: string): Promise<AveragePipelineTimeByStages[]> {
  const baseUrl = '/api/dashboard-metric/average-time-by-stage';
  const startISO = toStartOfDayISO(startDate);
  const endISO = toEndOfDayISO(endDate);
  const url = `${baseUrl}?startDate=${encodeURIComponent(startISO)}&endDate=${encodeURIComponent(endISO)}`;
  const { data } = await api.get<AverageTimeByStageApiItem[]>(url);
  return normalizeAverageTimeResponse(data ?? []);
}

function isToday(dateStr: string): boolean {
  return dateStr === new Date().toISOString().slice(0, 10);
}

export function useAveragePipelineTimeByStage(startDate: string, endDate: string) {
  const { addNotifications } = useSwobNotifications();
  const [hasError, setHasError] = useState(false);
  const endDateIsToday = isToday(endDate);

  const query = useQuery<AveragePipelineTimeByStages[]>({
    queryKey: ['average-pipeline-time-by-stage', startDate, endDate],
    queryFn: () => getAveragePipelineTimeByStage(startDate, endDate),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(startDate && endDate),
    refetchInterval: !hasError && endDateIsToday ? 60000 : false
  });

  useEffect(() => {
    if (query.error && !hasError) {
      setHasError(true);
      addNotifications([
        {
          title: 'Error - Dashboard Metrics',
          icon: 'icon-triangle-warning',
          description: 'An error occurred while fetching the average SW delivery time by stage.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  }, [query.error, hasError, addNotifications]);

  return query;
}
